import { LocalizedError, localizedMessageOf } from "@/lib/localized-error";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { useHasFeature } from "@/auth/access-features";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import type { SaveState } from "@/components/match/use-match-editor";
import { ShareDetailStatus } from "@/components/share/ShareDetailStatus";
import { userErrorMessage } from "@/lib/user-error";
import {
  allowedData,
  availableFacets,
  DASHBOARD_FACETS,
  DASHBOARD_KINDS,
  storedSections,
  type DashboardContext,
  type DashboardFacetKey,
  type DashboardKind,
  type KindConfig,
} from "./dashboard-kinds";
import type { Msg } from "./project-mock-data";
import { ProjectWorkspace, type ProjectActivityItem } from "./ProjectWorkspace";
import {
  useDashboardSource,
  type DashboardData,
  type DashboardEntity,
  type DashboardPatch,
  type DashboardSource,
} from "./use-dashboard-source";

// Das Dashboard eines Projekts, für jede Art dieselbe Hülle: Kopf mit Titel, Speicherzustand und
// Löschen, Übersicht, Reiter je Fläche, Einstellungen. Was je Art verschieden ist (Flächen, Felder der
// Übersicht, Einstellungen, Texte), kommt aus dashboard-kinds.tsx; woher das Objekt kommt und wohin
// es schreibt, aus dem Adapter in use-dashboard-source.ts. Diese Datei kennt keine Art beim Namen.

export function ProjectDashboard({ kind }: { kind: DashboardKind }) {
  const intl = useIntl();
  const config = DASHBOARD_KINDS[kind];
  const canUseOutsidePage = useHasFeature(config.outsideReaderFeature ?? "");
  const id = useParams()[config.idParam] ?? "";
  const source = useDashboardSource(kind, id);
  const entity = source.entity;

  if (canUseOutsidePage && !source.loading && !source.error && entity && !entity.isMember && config.outsideReaderPath) {
    return <Navigate to={config.outsideReaderPath(entity.id)} replace />;
  }

  return (
    <main className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      {entity ? (
        // Der Schlüssel setzt den Arbeitsstand neu auf, sobald ein anderes Objekt geladen ist; danach
        // gehört er dem Dashboard, nicht dem Cache.
        <DashboardBody key={entity.id} config={config} source={source} entity={entity} />
      ) : (
        // Mit Id in der Adresse gibt es nichts anzulegen: bis das Objekt da ist, zeigt die Seite Laden,
        // Fehler oder "nicht gefunden" wie ein Beitrag (spark#1001).
        <>
          <div className="mb-5">
            <BackButton />
          </div>
          <ShareDetailStatus
            loading={source.loading}
            error={source.error}
            notFoundLabel={intl.formatMessage(config.texts.notFound)}
            notFoundId="project-not-found"
          />
        </>
      )}
    </main>
  );
}

function DashboardBody({
  config,
  source,
  entity,
}: {
  config: KindConfig;
  source: DashboardSource;
  entity: DashboardEntity;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const [title, setTitle] = useState(entity.name);
  const [description, setDescription] = useState(entity.description);
  const [data, setData] = useState<DashboardData>(entity.data);
  /**
   * Die Flächen, die es ins gespeicherte Projekt geschafft haben. Teilmenge von `done`: dort stehen
   * auch die gerade geöffneten, in denen noch nichts steht.
   */
  const [sections, setSections] = useState<readonly DashboardFacetKey[]>(() => storedSections(config, entity.data));
  // Wer Inhalt in einer Fläche hat, hat die Fläche, auch wenn sie nie als Pille geholt wurde.
  const [done, setDone] = useState<ReadonlySet<DashboardFacetKey>>(
    () => new Set([...storedSections(config, entity.data), ...entity.filledFacets]),
  );
  const [activity, setActivity] = useState<readonly ProjectActivityItem[]>([]);
  // Welche Ansicht in einer Fläche offen ist. Hier oben, weil eine Fläche in eine andere springt und
  // dabei sagt, welche Ansicht sich dort öffnen soll; der Reiterwechsel soll sie nicht verlieren.
  const [views, setViews] = useState<Partial<Record<DashboardFacetKey, string | null>>>({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useTabParam();
  const autosave = useAutosave(source.save);
  const readOnly = !entity.actions.canUpdate;

  const lastServerRequirements = useRef(entity.data.matchRequirements);
  const { hasPendingData } = autosave;
  useEffect(() => {
    const requirements = entity.data.matchRequirements;
    if (requirements === lastServerRequirements.current) return;
    lastServerRequirements.current = requirements;
    // A local requirements draft wins over a concurrent agent response. Other edited fields do not
    // prevent adopting the agent's requirements, and a later save-state transition must not replay it.
    if (requirements === undefined || hasPendingData("matchRequirements")) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Merge an external Apollo update into the independent local draft.
    setData(current => ({ ...current, matchRequirements: requirements }));
  }, [entity.data.matchRequirements, hasPendingData]);

  /** Tippen: lokal sofort, gespeichert wird gebündelt. */
  function edit(patch: { title?: string; description?: string }) {
    if (patch.title !== undefined) setTitle(patch.title);
    if (patch.description !== undefined) setDescription(patch.description);
    autosave.schedule({
      ...(patch.title !== undefined ? { name: patch.title } : {}),
      ...(patch.description !== undefined ? { description: patch.description } : {}),
    });
  }

  /** Schreibt einen Ausschnitt des data-Dokuments; genesis mischt ihn in den Bestand. */
  function editData(patch: DashboardData) {
    const allowed = allowedData(config, patch);
    if (Object.keys(allowed).length === 0) return;
    setData((current) => ({ ...current, ...allowed }));
    autosave.schedule({ data: allowed });
  }

  /**
   * Nimmt Flächen dauerhaft ins Projekt auf. Aufgerufen wird das erst, wenn in einer Fläche etwas
   * entstanden ist oder jemand sie bewusst holt: wer nur hineinschaut, soll nach dem Neuladen keinen
   * leeren Reiter vorfinden. Mehrere Schlüssel auf einmal, weil einzeln nacheinander gerufen der zweite
   * Aufruf den `sections`-Stand des ersten überschriebe, der im selben Render noch alt ist.
   */
  function keepFacets(keys: readonly DashboardFacetKey[]) {
    setDone((prev) => new Set([...prev, ...keys]));
    // Wer das Objekt nicht ändern darf, holt eine Fläche nur für sich (etwa die Matches mit WRITE auf
    // `candidates` allein); gespeichert würde sie mit einem Schreiben, das genesis ablehnt.
    if (readOnly) return;
    const added = keys.filter((key) => !sections.includes(key));
    if (added.length === 0) return;
    const next = [...sections, ...added];
    setSections(next);
    editData({ sections: next });
  }

  function openFacet(key: DashboardFacetKey, view?: string | null) {
    setDone((prev) => new Set(prev).add(key));
    if (view !== undefined) setViews((prev) => ({ ...prev, [key]: view }));
    setActiveTab(key);
  }

  function log(id: string, message: Msg) {
    // Zeitpunkt wird beim Eintragen festgehalten, nicht beim Anzeigen: eine Zeile soll später dasselbe
    // Datum tragen wie in dem Moment, in dem sie entstand.
    setActivity((prev) =>
      prev.some((a) => a.id === id) ? prev : [...prev, { id, message, at: new Date().toISOString() }],
    );
  }

  const ctx: DashboardContext = {
    config,
    entity,
    source,
    title,
    description,
    data,
    readOnly,
    activity,
    flush: async () => {
      const error = await autosave.flush();
      if (error) throw error;
    },
    editData,
    keepFacets,
    openFacet,
    log,
  };

  const facets = availableFacets(config, {
    isMember: entity.isMember,
    canLinkMatches: Boolean(source.linkMatch),
  });
  const isShown = (key: DashboardFacetKey) => config.alwaysFacets.includes(key) || done.has(key);
  // Reiter: Übersicht plus jede Fläche, die im Projekt liegt, in der Reihenfolge der Art, damit die
  // Leiste nicht je nach Klickfolge anders aussieht.
  const tabs = [
    { id: "overview", label: intl.formatMessage({ id: "project.workspace.overview", defaultMessage: "Übersicht" }) },
    ...facets.filter(isShown).map((key) => ({ id: key, label: intl.formatMessage(DASHBOARD_FACETS[key].label) })),
  ];
  // Eine Fläche holt, wer sie füllen darf: die Matches, wer Matches verknüpfen darf, alle übrigen,
  // wer das Objekt ändern darf.
  const canAdd = (key: DashboardFacetKey) =>
    DASHBOARD_FACETS[key].needsMatchLinks ? entity.actions.canManageMatches : !readOnly;
  const pills = facets
    .filter((key) => !isShown(key) && canAdd(key))
    .map((key) => {
      const facet = DASHBOARD_FACETS[key];
      return { key, label: intl.formatMessage(config.facetAddLabels?.(data)[key] ?? facet.addLabel ?? facet.label) };
    });
  // Eine Adresse mit `?tab=` öffnet die Fläche auch dann, wenn sie (noch) keinen Reiter hat. Was diese
  // Person bei dieser Art gar nicht bekommt (oder was es nicht gibt), öffnet die Übersicht.
  const openFacetKey = facets.find((key) => key === activeTab);
  const shownTab = openFacetKey ?? "overview";
  const { OverviewFields, FacetView, Settings, HeaderMeta } = config;

  async function remove() {
    if (deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await source.remove();
    } catch (e) {
      // Nie die rohe Servermeldung zeigen (@adr SPARK-0010-no-raw-server-errors-in-ui@1).
      setDeleteError(localizedMessageOf(e) ?? userErrorMessage(intl, e));
      setDeleting(false);
      return;
    }
    setConfirmDelete(false);
    navigate(config.afterDelete.path, { replace: config.afterDelete.replace });
  }

  return (
    <>
      <ProjectWorkspace
        project={{ title, description }}
        texts={config.texts}
        readOnly={readOnly}
        company={entity.company}
        readOnlyReason={entity.readOnlyReason}
        actingAsCompanyName={entity.actingAsCompanyName}
        onEdit={edit}
        onCommit={() => void autosave.flush()}
        saveState={autosave.saveState}
        saveError={autosave.saveError}
        lastSavedAt={autosave.lastSavedAt}
        onRetrySave={() => void autosave.flush()}
        activity={activity}
        createdMeta={entity.createdBy ? { at: entity.createdAt ?? "", by: entity.createdBy } : null}
        headerMeta={HeaderMeta ? <HeaderMeta ctx={ctx} /> : null}
        overviewFields={<OverviewFields ctx={ctx} onOpenSettings={config.settingsAvailable(entity) ? () => setSettingsOpen(true) : undefined} />}
        pills={pills}
        showServices={config.showServices}
        onShowActivity={() => {
          keepFacets(["ablauf"]);
          setActiveTab("ablauf");
        }}
        onAddBlock={(key) => {
          // Hinzufügen heißt hingehen: die neue Fläche wird gleich geöffnet. Und sie BLEIBT, auch wenn
          // man nichts einträgt: wer eine Fläche holt, hat eine Entscheidung getroffen.
          const facet = facets.find((candidate) => candidate === key);
          if (!facet) return;
          keepFacets([facet]);
          setActiveTab(facet);
        }}
        onDelete={entity.actions.canDelete ? () => setConfirmDelete(true) : undefined}
        onOpenSettings={config.settingsAvailable(entity) ? () => setSettingsOpen(true) : undefined}
        tabs={tabs}
        activeTab={shownTab}
        onSelectTab={setActiveTab}
      >
        {openFacetKey ? (
          <FacetView
            facet={openFacetKey}
            ctx={ctx}
            view={views[openFacetKey] ?? null}
            onViewChange={(next) => setViews((prev) => ({ ...prev, [openFacetKey]: next }))}
          />
        ) : null}
      </ProjectWorkspace>

      {settingsOpen ? (
        <Settings
          ctx={ctx}
          onSaveBasics={async (next) => {
            edit(next);
            if (next.data) editData(next.data);
            const error = await autosave.flush();
            if (error) throw error;
          }}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}

      {/* Löschen ist endgültig und betrifft das ganze Objekt, deshalb die Rückfrage im Dialog statt der
          zweistufigen Inline-Variante (@adr SPARK-0011-destructive-actions-require-confirmation@1).
          Verknüpfte Matches bleiben: sie sind eigene Objekte mit eigener Arbeit darin. */}
      <ConfirmDialog
        id="project-delete-dialog"
        open={confirmDelete}
        title={intl.formatMessage(config.texts.deleteTitle)}
        message={
          <>
            {intl.formatMessage(config.texts.deleteConfirm)}
            {deleteError && (
              <p id="project-delete-dialog-error" role="alert" className="mt-3">
                {deleteError}
              </p>
            )}
          </>
        }
        confirmLabel={<FormattedMessage id="common.delete" defaultMessage="Löschen" />}
        onConfirm={remove}
        onCancel={() => {
          if (deleting) return;
          setConfirmDelete(false);
          setDeleteError(null);
        }}
      />
    </>
  );
}

/**
 * Der offene Reiter steht in der Adresse, nicht nur im Zustand. Sonst landet man nach einem Abstecher
 * aufs Profil einer Person über den Zurück-Knopf wieder in der Übersicht. `replace`, damit die
 * Reiterwechsel keine eigene Geschichte anlegen, durch die der Zurück-Knopf sich erst klicken müsste.
 */
function useTabParam(): [string, (next: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "overview";
  const setActiveTab = useCallback(
    (next: string) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next === "overview") params.delete("tab");
          else params.set("tab", next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  return [activeTab, setActiveTab];
}

/**
 * Autosave nach demselben Muster wie der Match-Editor (@adr SPARK-0008-match-editor-autosave@1): eine
 * Änderung plant einen Schreibvorgang etwa eine Sekunde nach der letzten Eingabe, das Verlassen eines
 * Feldes zieht ihn vor. Die Zustandsanzeige ist dieselbe Komponente wie dort.
 *
 * Eigener Code statt `useMatchEditor`: der steckt voller Match-Wissen. Herausgelöst bliebe davon ein
 * Debounce mit vier Zuständen, und der steht hier, für jede Art von Projekt einmal.
 */
function useAutosave(save: (patch: DashboardPatch) => Promise<void>) {
  const intl = useIntl();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<DashboardPatch>({});
  const activePatch = useRef<DashboardPatch | null>(null);
  const inFlight = useRef<Promise<LocalizedError | null> | null>(null);

  const hasPendingData = useCallback((key: string) =>
    Object.hasOwn(pending.current.data ?? {}, key) || Object.hasOwn(activePatch.current?.data ?? {}, key), []);

  const flush = useCallback(async (): Promise<LocalizedError | null> => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    while (inFlight.current) {
      const error = await inFlight.current;
      if (error) return error;
    }
    const patch = pending.current;
    if (Object.keys(patch).length === 0) return null;
    pending.current = {};
    activePatch.current = patch;
    setSaveState("saving");
    setSaveError(null);
    const saving = save(patch).then(() => {
      setLastSavedAt(Date.now());
      setSaveState(Object.keys(pending.current).length > 0 ? "pending" : "idle");
      return null;
    }, (error) => {
      setSaveError(localizedMessageOf(error) ?? userErrorMessage(intl, error));
      pending.current = mergeDashboardPatch(patch, pending.current);
      setSaveState("error");
      return new LocalizedError(localizedMessageOf(error) ?? userErrorMessage(intl, error), { cause: error });
    });
    inFlight.current = saving;
    const error = await saving;
    activePatch.current = null;
    inFlight.current = null;
    return error;
  }, [save, intl]);

  const flushRef = useRef(flush);
  useEffect(() => { flushRef.current = flush; }, [flush]);
  useEffect(() => () => {
    if (debounceTimer.current) void flushRef.current();
  }, []);
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (Object.keys(pending.current).length === 0 && !inFlight.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const schedule = useCallback(
    (patch: DashboardPatch) => {
      // data-Ausschnitte werden auch lokal zusammengeführt, sonst verlöre ein zweiter Tastendruck den
      // ersten, bevor überhaupt gesendet wird.
      pending.current = mergeDashboardPatch(pending.current, patch);
      setSaveState("pending");
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => void flush(), 1000);
    },
    [flush],
  );

  return { saveState, saveError, lastSavedAt, flush, schedule, hasPendingData };
}

function mergeDashboardPatch(earlier: DashboardPatch, later: DashboardPatch): DashboardPatch {
  return { ...earlier, ...later,
    ...(earlier.data || later.data ? { data: { ...earlier.data, ...later.data } } : {}),
  };
}
